import Image from 'next/image'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {brandColors} from '@/products/flexible-web-toolkit/colors/registry'
import {loadImageFromKontentAI} from '@/image-loader'
import type {Block, Tersed} from '@/services/kontent-ai/types'
import {
	ContentBlockTab,
	TabbedContentBlock,
} from '@/products/flexible-web-toolkit/components/tabbed-content-block/tabbed-content-block'
import {pushToDataLayer} from '@/utilities/analytics'
import {spacing} from '@/products/flexible-web-toolkit/styles'
import {colorWithFallback as resolveColorWithFallback} from '@/utilities/hooks/color-with-fallback'
import {RichTextRenderer} from '../../components/rich-text-renderer'

type TabbedContentBlockFlexibleTabBlock = IContentItem<{
	title: Elements.TextElement
	enableScrollbar: Elements.MultipleChoiceElement
	tabBlocks: Elements.LinkedItemsElement
	/** @deprecated Use brandColor instead. Retained for backward compatibility. */
	tabColor: Elements.MultipleChoiceElement
	/** New custom element color picker – preferred source for tab color */
	brandColor: Elements.CustomElement
	snippetSpacingSpacing: Elements.MultipleChoiceElement
}>

type TabbedContentBlockTabContentItem = IContentItem<{
	title: Elements.TextElement
	body: Elements.RichTextElement
	enableScrollbar: Elements.MultipleChoiceElement
	image: Elements.AssetsElement
	/** @deprecated Use brandColor instead. Retained for backward compatibility. */
	tabColor: Elements.MultipleChoiceElement
	/** New custom element color picker – preferred source for tab color */
	brandColor: Elements.CustomElement
	snippetSpacingSpacing: Elements.MultipleChoiceElement
}>

export type TabbedContentBlockContentItem = IContentItem<{
	tabs: Elements.LinkedItemsElement<
		TabbedContentBlockTabContentItem | TabbedContentBlockFlexibleTabBlock
	>
	tabStyle: Elements.MultipleChoiceElement
	snippetSpacingSpacing: Elements.MultipleChoiceElement
}>

export const TabbedContentBlockBlock: Block<TabbedContentBlockContentItem> = ({
	block,
	BlockMapper,
	...context
}) => {
	const componentSpacing =
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- condition is necessary, block.elements.snippetSpacingSpacing may be null
		spacing[
			block.elements.snippetSpacingSpacing[0]
				?.codename as keyof typeof spacing
		] || spacing.none
	return (
		<div className={`${componentSpacing} rounded-lg shadow-md`}>
			<TabbedContentBlock
				onChange={(index) => {
					const tabIndex = Number(index)
					const selectedTab = block.elements.tabs[tabIndex]
					if (!selectedTab) return
					const titleText = selectedTab.elements.title
					pushToDataLayer({
						event: 'cta_click',
						cta_name: titleText,
						cta_category: 'CTA_Button',
					})
				}}
				tabColorStyles={brandColors}
				tabMobileView={
					(block.elements.tabStyle[0]?.codename as
						| 'stack'
						| 'scroll'
						| undefined) ?? 'stack'
				}
			>
				{block.elements.tabs.map((tab) => {
					const disableScrollbar =
						tab.elements.enableScrollbar[0]?.name === 'No'
					if ('body' in tab.elements) {
						const standardTab =
							tab as Tersed<TabbedContentBlockTabContentItem>

						const {colorId: standardTabColor} =
							resolveColorWithFallback({
								blockColor: standardTab.elements.brandColor,
								blockColorLegacy: standardTab.elements.tabColor,
								pageConfig: context.page.elements.config[0],
								fallbackColorId: 'black',
							})

						return (
							<ContentBlockTab
								color={{
									choice: standardTabColor ?? undefined,
								}}
								disableOverflow
								key={standardTab.system.id}
								title={
									<span
										data-kontent-element-codename="title"
										data-kontent-item-id={
											standardTab.system.id
										}
									>
										{standardTab.elements.title}
									</span>
								}
							>
								{standardTab.elements.image[0] ? (
									<div
										className="md:flex"
										data-kontent-item-id={
											standardTab.system.id
										}
									>
										<div className="pb-6 md:w-1/2 md:pb-0 md:pr-8">
											<RichTextRenderer
												BlockMapper={BlockMapper}
												className="content break-words"
												data-kontent-element-codename="body"
												element={
													standardTab.elements.body
												}
												{...context}
											/>
										</div>
										<div className="pb-2 md:w-1/2 md:pl-8">
											<Image
												alt={
													standardTab.elements
														.image[0].description ??
													''
												}
												data-kontent-element-codename="image"
												height={
													standardTab.elements
														.image[0].height || 334
												}
												loader={loadImageFromKontentAI}
												src={
													standardTab.elements
														.image[0].url
												}
												width={
													standardTab.elements
														.image[0].width || 502
												}
											/>
										</div>
									</div>
								) : null}
							</ContentBlockTab>
						)
					}

					const flexibleTab =
						tab as Tersed<TabbedContentBlockFlexibleTabBlock>
					const {colorId: flexibleTabColor} =
						resolveColorWithFallback({
							blockColor: flexibleTab.elements.brandColor,
							blockColorLegacy: flexibleTab.elements.tabColor,
							pageConfig: context.page.elements.config[0],
							fallbackColorId: 'black',
						})

					return (
						<ContentBlockTab
							color={{
								choice: flexibleTabColor ?? undefined,
							}}
							disableOverflow={disableScrollbar}
							key={tab.system.id}
							title={
								<span
									data-kontent-element-codename="title"
									data-kontent-item-id={tab.system.id}
								>
									{tab.elements.title}
								</span>
							}
						>
							<BlockMapper
								blocks={flexibleTab.elements.tabBlocks}
								{...context}
							/>
						</ContentBlockTab>
					)
				})}
			</TabbedContentBlock>
		</div>
	)
}
